import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, MapPin, ChevronRight, CalendarDays } from "lucide-react";
import { addDays, format } from "date-fns";
import { es } from "date-fns/locale";
import { DayTabs } from "@/components/DayTabs";

const STATUS = {
    pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    in_progress: { label: "En progreso", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    completed: { label: "Completada", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    not_done: { label: "No realizada", cls: "bg-red-100 text-red-800 border-red-200" },
};

export default function AuditRoute() {
    const nav = useNavigate();
    const [routesByDate, setRoutesByDate] = useState({});
    const [clients, setClients] = useState({});
    const [branches, setBranches] = useState({});
    const [loading, setLoading] = useState(true);
    const today = new Date().toISOString().slice(0, 10);
    const dates = [0, 1, 2].map((i) => format(addDays(new Date(), i), "yyyy-MM-dd"));
    const [selectedDate, setSelectedDate] = useState(today);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [routes, cRes] = await Promise.all([
                    Promise.all(dates.map((d) => api.get(`/schedules?date=${d}`))),
                    api.get("/clients"),
                ]);
                const cmap = {};
                (cRes.data || []).forEach((c) => { cmap[c.id] = c; });
                setClients(cmap);
                const byDate = {};
                const allSvcs = [];
                routes.forEach((r, idx) => {
                    const svcs = (r.data || []).sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
                    byDate[dates[idx]] = svcs;
                    allSvcs.push(...svcs);
                });
                setRoutesByDate(byDate);
                // Resolve branch (sucursal) addresses across all days
                const branchIds = [...new Set(allSvcs.map((s) => s.branch_id).filter(Boolean))];
                if (branchIds.length) {
                    const bmap = {};
                    await Promise.all(branchIds.map(async (bid) => {
                        try { const r = await api.get(`/branches/${bid}`); bmap[bid] = r.data; } catch { /* ignore */ }
                    }));
                    setBranches(bmap);
                }
            } catch {
                toast.error("No se pudo cargar la ruta");
            } finally { setLoading(false); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [today]);

    const isToday = selectedDate === today;
    const services = routesByDate[selectedDate] || [];
    const counts = dates.reduce((acc, d) => { acc[d] = (routesByDate[d] || []).length; return acc; }, {});

    return (
        <div className="p-4" data-testid="audit-route-page">
            <div className="flex items-center gap-2 mb-1">
                <ClipboardCheck className="w-5 h-5 text-[hsl(var(--trulyn-navy))]" />
                <h1 className="text-xl font-semibold">Mi ruta del día</h1>
            </div>
            <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1 capitalize">
                <CalendarDays className="w-3.5 h-3.5" />
                {format(new Date(selectedDate + "T00:00:00"), "EEEE d 'de' MMMM", { locale: es })}
            </p>

            <DayTabs value={selectedDate} onChange={setSelectedDate} counts={counts} />

            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl bg-secondary animate-pulse" />)}
                </div>
            ) : services.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground text-sm" data-testid="audit-empty">
                    {isToday ? "No tienes auditorías programadas para hoy." : "No tienes auditorías programadas para este día."}
                </Card>
            ) : (
                <div className="space-y-3">
                    {services.map((s) => {
                        const c = clients[s.client_id] || {};
                        const b = s.branch_id ? branches[s.branch_id] : null;
                        const addr = (b && (b.address || b.city)) || c.address || c.city || "";
                        const st = STATUS[s.status] || STATUS.pending;
                        return (
                            <Card
                                key={s.id}
                                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => nav(`/audit/service/${s.id}`)}
                                data-testid={`audit-service-${s.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                        <div className="text-center shrink-0">
                                            <div className="font-mono text-lg font-semibold text-[hsl(var(--trulyn-navy))]">{s.scheduled_time}</div>
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-sm leading-tight">{c.name || "Cliente"}</div>
                                            {b?.name && (
                                                <div className="text-xs font-medium text-foreground/80 mt-0.5 truncate">{b.name}</div>
                                            )}
                                            <div className="text-xs text-muted-foreground flex items-start gap-1 mt-0.5" data-testid={`audit-service-${s.id}-address`}>
                                                <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                                                <span className="line-clamp-2">{addr || "Dirección no registrada"}</span>
                                            </div>
                                            <Badge variant="outline" className={`text-[10px] mt-1.5 ${st.cls}`}>{st.label}</Badge>
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
